import React, { useEffect, useMemo, useState } from "react";
import { Form, Col, Button, Container, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { debounce } from "lodash";
import LoadingOverlay from "../common/LoadingOverlay";
import SuperUserService from "../../api-service/SuperUserService";
import StorageHelper from "../../helpers/StorageHelper";
import SessionHelper from "../../helpers/SessionHelper";
import { GET_FILTERED_FIELDS_TO_TRANSFER } from "../../configurations/Urls";
import Alert from "../common/Alert";
import {
  EMPTY_FIELD_NAME_VALIDATION,
  FIELD_TRANSFER_BANNER,
  FIELD_TRANSFER_VALIDATION,
  INCOMING_TOKEN_HAS_EXPIRED,
  SAME_FARMER_VALIDATION,
  STAFF_AUTHORIZED,
  UNIQUE_FIELD_NAME_VALIDATION,
  FIELD_TRANSFER_SUCCESSFUL,
  ERROR_FETCHING_FARMER_LIST,
} from "../../configurations/MessageConstants";
import MessageBanner from "../common/MessageBanner";
import {
  DANGER,
  STAFF_SESSION_EXPIRED,
  FIELD_TRANSFER_CUSTOM_STYLES,
  EMPTY,
} from "../../configurations/Constants";
import { StatusCodes } from "../../configurations/status-codes";

const SearchBox = ({ onSearch, value }) => (
  <Form.Control
    type="text"
    defaultValue={value}
    name="searchValue"
    onChange={onSearch}
    placeholder="Search"
    className="form-control form-control-lg"
  />
);
function FieldTransfer() {
  //To Farmer
  const [toFarmer, setToFarmer] = useState(null);
  const [toBusinessList, setToBusinessList] = useState([]);
  const [toFarmList, setToFarmList] = useState([]);
  const [toInitialFarmList, setToInitialFarmList] = useState([]);
  const [toSelectedBusiness, setToSelectedBusiness] = useState(null);
  const [toSelectedFarm, setToSelectedFarm] = useState(null);
  //From Farmer
  const [fromFarmer, setFromFarmer] = useState(null);
  const [fromBusinessList, setFromBusinessList] = useState([]);
  const [fromFarmList, setFromFarmList] = useState([]);
  const [fromInitialFarmList, setFromInitialFarmList] = useState([]);
  const [fromSelectedBusinessList, setFromSelectedBusinessList] = useState([]);
  const [fromSelectedFarmList, setFromSelectedFarmList] = useState([]);

  const [farmersList, setFarmersList] = useState([]);
  const [searchValue, setSearchValue] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [column, setColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(DANGER);
  const [alertMessage, setAlertMessage] = useState(EMPTY);
  const [validated, setValidated] = useState(false);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [overlayLoadingText, setOverlayLoadingText] = useState(
    "Loading Farmer List..."
  );
  const [fieldTransferSuccessMessage, setFieldTransferSuccessMessage] =
    useState(EMPTY);
  const [fieldTransferSuccessModal, setFieldTransferSuccessModal] =
    useState(false);

  const columns = [
    {
      id: 1,
      name: "Field",
      selector: (row) => row.field_name,
      cell: (row) => (
        <Form.Control
          type="text"
          defaultValue={row.field_name}
          name="fieldName"
          maxLength="40"
          onChange={(e) => {
            e.preventDefault();
            row.field_name = e.target.value;
          }}
          className="form-control form-control-sm"
          disabled={
            !selectedFields.some((field) => field.field_id === row.field_id)
          }
        />
      ),
      sortable: true,
      reorder: true,
      value: "field_name",
    },
    {
      id: 2,
      name: "Acres",
      selector: (row) => row.acres,
      sortable: true,
      reorder: true,
      value: "acres",
    },
    {
      id: 3,
      name: "Business",
      selector: (row) => row.business_name,
      sortable: true,
      reorder: true,
      value: "business_name",
    },
    {
      id: 4,
      name: "Farm",
      selector: (row) => row.farm_name,
      sortable: true,
      reorder: true,
      value: "farm_name",
    },
  ];

  useEffect(() => {
    (async function () {
      try {
        setOverlayLoading(true);
        const { data } = await SuperUserService.getFarmerList();
        setFarmersList(data.data.users);
        setOverlayLoading(false);
      } catch (error) {
        console.error(ERROR_FETCHING_FARMER_LIST, error);
        setOverlayLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    fetchFilteredFields(
      currentPage,
      perPage,
      fromFarmer,
      fromSelectedBusinessList,
      fromSelectedFarmList,
      searchValue,
      column,
      columnOrder
    );
  }, [
    currentPage,
    perPage,
    fromFarmer,
    fromSelectedBusinessList,
    fromSelectedFarmList,
    searchValue,
    column,
    columnOrder,
  ]);

  const fetchFilteredFields = async (
    page,
    size,
    farmer,
    businesses,
    farms,
    searchValue,
    column,
    columnOrder
  ) => {
    setSelectedFields([]);
    setClearSelectedRows(!clearSelectedRows);
    if (!farmer) {
      setData([]);
      return;
    }
    setLoading(true);
    let params = `?current_pg=${page}&rows_per_pg=${size}&user_email=${farmer.value}`;
    if (businesses.length > 0) {
      let businessNameList = businesses.map((business) => business.value);
      params = `${params}&businesses=${businessNameList.join(",")}`;
    }
    if (farms.length > 0) {
      let farmNameList = farms.map((farm) => farm.value);
      params = `${params}&farms=${farmNameList.join(",")}`;
    }
    if (searchValue) params = `${params}&search_text=${searchValue}`;
    if (column !== null) params = `${params}&column=${column}`;
    if (columnOrder !== null) params = `${params}&column_order=${columnOrder}`;

    const url = `${GET_FILTERED_FIELDS_TO_TRANSFER}${params}`;
    try {
      const response = await SuperUserService.getFilteredFieldsToTransfer(url);
      let fieldData = response.data.data;
      if (fieldData) {
        setData(fieldData.fields);
        setTotalRows(fieldData.totalRows);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      const responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const HandleSelectToFarmer = (farmer) => {
    if (!farmer) {
      setToFarmer(null);
      setToBusinessList([]);
      setToFarmList([]);
      setToInitialFarmList([]);
      setToSelectedBusiness(null);
      setToSelectedFarm(null);
      return;
    }
    setToFarmer(farmer);
    setToSelectedBusiness(null);
    setToSelectedFarm(null);
    getFarmsAndBusinesses(farmer, "ToFarmer");
  };
  const HandleSelectFromFarmer = (farmer) => {
    if (!farmer) {
      setFromFarmer(null);
      setFromBusinessList([]);
      setFromFarmList([]);
      setFromInitialFarmList([]);
      setFromSelectedBusinessList([]);
      setFromSelectedFarmList([]);
      return;
    }
    setFromFarmer(farmer);
    setFromSelectedBusinessList([]);
    setFromSelectedFarmList([]);
    setCurrentPage(1);
    setPerPage(10);
    setColumn(null);
    setColumnOrder(null);
    getFarmsAndBusinesses(farmer, "FromFarmer");
  };

  const getFarmsAndBusinesses = async (farmer, farmerType) => {
    try {
      setOverlayLoading(true);
      setOverlayLoadingText("Loading Businesses and Farms...");
      const response = await SuperUserService.getFarmerBusinessAndFarms(
        farmer.value
      );
      const farmsAndBusiness = response.data.data.details;
      const businesses = farmsAndBusiness.businesses.map((business) => ({
        value: business,
        label: business,
      }));
      const farms = farmsAndBusiness.farms_and_businesses.map((item) => ({
        value: item.farm_name,
        label: item.farm_name,
        business: item.business_name,
      }));
      if (farmerType === "ToFarmer") {
        setToBusinessList(businesses);
        setToFarmList(farms);
        setToInitialFarmList(farms);
      } else if (farmerType === "FromFarmer") {
        setFromBusinessList(businesses);
        setFromFarmList(farms);
        setFromInitialFarmList(farms);
      }
      setOverlayLoading(false);
    } catch (error) {
      setOverlayLoading(false);
      const responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const HandleToBusinessChange = async (business) => {
    let filteredFarmList = [];
    setToSelectedFarm(null);
    if (!business) {
      setToSelectedBusiness(null);
      setToFarmList(toInitialFarmList);
      return;
    }
    setToSelectedBusiness(business);
    const list = toInitialFarmList.filter(
      (farm) => farm.business === business.value
    );
    filteredFarmList.push(...list);
    setToFarmList(filteredFarmList);
  };

  const HandleToFarmChange = async (farms) => {
    if (!farms) {
      setToSelectedFarm(null);
      return;
    }
    setToSelectedFarm(farms);
  };

  const HandleFromBusinessChange = async (businesses) => {
    let filteredFarmList = [];
    let businessNameList = [];
    setFromSelectedFarmList([]);
    if (!businesses || !businesses.length) {
      setFromSelectedBusinessList([]);
      setFromFarmList(fromInitialFarmList);
      return;
    }
    setFromSelectedBusinessList(businesses);
    businesses.forEach((business) => {
      businessNameList.push(business.value);
      const list = fromInitialFarmList.filter(
        (farm) => farm.business === business.value
      );
      filteredFarmList.push(...list);
    });
    setFromFarmList(filteredFarmList);
  };

  const HandleFromFarmChange = async (farms) => {
    if (!farms || !farms.length) {
      setFromSelectedFarmList([]);
      return;
    }
    setFromSelectedFarmList(farms);
  };

  const handleSearchValue = async (e) => {
    await debouncedSearch(e.target.value);
  };

  const debouncedSearch = debounce(async (value) => {
    setSearchValue(value);
  }, 400);

  const handleSort = async (column, sortDirection) => {
    setColumn(column.value);
    setColumnOrder(sortDirection);
  };

  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedFields(selectedRows);
  };

  const HandleFieldTransfer = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setValidated(true);
    if (!toFarmer || !toSelectedFarm) {
      setShowAlert(true);
      setAlertType(DANGER);
      setAlertMessage(FIELD_TRANSFER_VALIDATION);
      return;
    } else if (toFarmer.value === fromFarmer.value) {
      setShowAlert(true);
      setAlertType(DANGER);
      setAlertMessage(SAME_FARMER_VALIDATION);
      return;
    } else if (selectedFields.some((field) => field.field_name === EMPTY)) {
      setShowAlert(true);
      setAlertType(DANGER);
      setAlertMessage(EMPTY_FIELD_NAME_VALIDATION);
      return;
    } else if (!checkUniqueFieldNames(selectedFields, "field_name")) {
      setShowAlert(true);
      setAlertType(DANGER);
      setAlertMessage(UNIQUE_FIELD_NAME_VALIDATION);
      return;
    }
    setOverlayLoading(true);
    setOverlayLoadingText("Transfering Fields...");
    try {
      const payload = {
        user_email: toFarmer.value,
        farm_to: toSelectedFarm.value,
        fields: selectedFields,
      };
      await SuperUserService.transferFields(payload);
      setOverlayLoading(false);
      const stringOfFields = selectedFields
        .map((field) => field.field_name)
        .join(", ");
      setFieldTransferSuccessMessage(
        <span>
          The {selectedFields.length === 1 ? "field" : "fields"}
          {" ( "}
          <strong>{stringOfFields}</strong>
          {" ) "}
          {selectedFields.length === 1 ? "was" : "were"} transferred
          successfully from <b>{fromFarmer.name}</b> to <b>{toFarmer.name}</b>
        </span>
      );
      HandleSelectFromFarmer(null);
      HandleSelectToFarmer(null);
      setFieldTransferSuccessModal(true);
      setValidated(false);
      closeAlert();
    } catch (error) {
      const responseData = error.response.data;
      setOverlayLoading(false);
      setShowAlert(true);
      setAlertType(DANGER);
      setAlertMessage(responseData.message);
      setValidated(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const checkUniqueFieldNames = (fields, field_name) => {
    const values = new Set();
    for (const field of fields) {
      if (values.has(field[field_name])) return false;
      values.add(field[field_name]);
    }
    return true;
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const contextActions = useMemo(() => {
    return (
      <Button type="button" onClick={() => HandleFieldTransfer()}>
        Field Transfer
      </Button>
    );
  }, [selectedFields, toFarmer, toSelectedFarm]);

  const actions = (
    <div className="d-flex justify-content-end flex-grow-1 ms-0 field-transfer-actions">
      <SearchBox onSearch={(e) => handleSearchValue(e, searchValue)} />
    </div>
  );

  return (
    <div className="field-transfer">
      <LoadingOverlay
        showSpinner={true}
        active={overlayLoading}
        text={overlayLoadingText}
      />
      {showAlert && (
        <Alert
          key="messageAlert"
          variant={alertType}
          closeAlert={(e) => closeAlert(e)}
          alertMessage={alertMessage}
          dismissible
          className="m-0"
        />
      )}
      <MessageBanner
        title={"Field Transfer"}
        description={`${FIELD_TRANSFER_BANNER} ${STAFF_AUTHORIZED}`}
      />
      <Container className="p-3 field-transfer-body">
        <div className="field-transfer-to-farmer-container">
          <h3>Transfer To</h3>
          <h5>Select farmer and farm to transfer fields to</h5>
          <Row>
            <Form.Group as={Col} md={6} controlId="formFarmerSelect">
              <Select
                name="toFarmerData"
                options={farmersList}
                isClearable={true}
                className={`basic-multi-select 
                ${
                  validated
                    ? !toFarmer || toFarmer?.value === fromFarmer?.value
                      ? "field-transfer-validation"
                      : "field-transfer-validation-success"
                    : EMPTY
                }`}
                onChange={HandleSelectToFarmer}
                value={toFarmer}
                placeholder="Select a farmer"
              />
            </Form.Group>
            <Form.Group as={Col} controlId="FormBusinessList" md={3}>
              <Select
                name="toBusinessList"
                isClearable={true}
                options={toBusinessList}
                value={toSelectedBusiness}
                className="basic-multi-select"
                onChange={HandleToBusinessChange}
                placeholder="Select Business"
              />
            </Form.Group>
            <Form.Group as={Col} controlId="FormFarmList" md={3}>
              <Select
                name="farmList"
                isClearable={true}
                options={toFarmList}
                value={toSelectedFarm}
                className={`basic-multi-select 
                ${
                  validated && !toSelectedFarm
                    ? "field-transfer-validation"
                    : validated && toSelectedFarm
                    ? "field-transfer-validation-success"
                    : EMPTY
                }`}
                onChange={HandleToFarmChange}
                placeholder="Select Farms"
              />
            </Form.Group>
          </Row>
        </div>
        <Row className="field-transfer-from-farmer-container">
          <DataTable
            className="pb-3"
            title={
              <div>
                <h3>Transfer From</h3>
                <h5>Filter and search for fields to transfer</h5>
              </div>
            }
            contextComponent={
              <>
                {selectedFields.length === 1
                  ? `${selectedFields.length} field selected`
                  : `${selectedFields.length} fields selected`}
                {contextActions}
              </>
            }
            clearSelectedRows={clearSelectedRows}
            actions={actions}
            noDataComponent="There are no fields to display"
            customStyles={FIELD_TRANSFER_CUSTOM_STYLES}
            columns={columns}
            data={data}
            progressPending={loading}
            pagination
            paginationServer
            onSort={handleSort}
            paginationRowsPerPageOptions={[10, 25, 50, 100, 150]}
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangeRowsPerPage={setPerPage}
            onChangePage={setCurrentPage}
            selectableRowsNoSelectAll
            selectableRows
            subHeader
            subHeaderComponent={
              <div className="w-100 mb-2">
                <Row>
                  <Form.Group as={Col} md={6} controlId="formFarmerSelect">
                    <Select
                      name="fromFarmerData"
                      menuPlacement="top"
                      options={farmersList}
                      isClearable={true}
                      className="basic-multi-select"
                      onChange={HandleSelectFromFarmer}
                      value={fromFarmer}
                      placeholder="Select a farmer"
                    />
                  </Form.Group>
                  <Form.Group as={Col} controlId="FormBusinessList" md={3}>
                    <Select
                      name="fromBusinessList"
                      menuPlacement="top"
                      isMulti
                      options={fromBusinessList}
                      value={fromSelectedBusinessList}
                      className="basic-multi-select"
                      onChange={HandleFromBusinessChange}
                      placeholder="Select Business"
                    />
                  </Form.Group>
                  <Form.Group as={Col} controlId="FormFarmList" md={3}>
                    <Select
                      name="farmList"
                      menuPlacement="top"
                      isMulti
                      options={fromFarmList}
                      value={fromSelectedFarmList}
                      className="basic-multi-select"
                      onChange={HandleFromFarmChange}
                      placeholder="Select Farms"
                    />
                  </Form.Group>
                </Row>
              </div>
            }
            onSelectedRowsChange={handleSelectedRowsChange}
          />
        </Row>
      </Container>
      <Modal
        size="md"
        show={fieldTransferSuccessModal}
        centered
        onHide={() => setFieldTransferSuccessModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {FIELD_TRANSFER_SUCCESSFUL}
            <i
              className="fa fa-check field-transfer-success-icon"
              aria-hidden="true"
            ></i>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">{fieldTransferSuccessMessage}</p>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default FieldTransfer;
